import React, { useCallback, useEffect, useRef, useState } from 'react';
import askApi from '../services/askApi';
import enrichmentApi from '../services/enrichmentApi';
import Icon from './Icon';
import './AskSinatra.css';

const EXAMPLES = ['CxOs at Inference.net', 'Connections from Mukundhan', 'Tier A GPU clouds in India'];
const LOGO_URL = `${process.env.REACT_APP_API_BASE_URL || ''}/api/v2/logos`;
const TIER_LABELS = { S: 'Strategic' };

const initials = (name) => name.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase();

const headline = ({ kind, total }) => {
  const noun = kind === 'companies'
    ? (total === 1 ? 'company' : 'companies')
    : (total === 1 ? 'person' : 'people');
  return total ? `${total.toLocaleString()} ${noun}` : `No ${kind === 'companies' ? 'companies' : 'people'} match`;
};

const isTyping = (element) => element?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element?.tagName);

// Graph/Tree/Pipeline only filter by company name and person name today - everything else the
// model can derive (seniority, function, location, title, icp, has_phone, has_email) has no
// view-level equivalent yet. Ask Sinatra's own results list is always the complete, correct
// answer regardless; this only explains when the active view can show just part of it.
const VIEW_MAPPED_FIELDS = new Set(['companies', 'person', 'kind']);

const unmappedFilterNote = (filters) => {
  const extra = Object.keys(filters || {}).filter(key => {
    if (VIEW_MAPPED_FIELDS.has(key)) return false;
    const value = filters[key];
    return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
  });
  if (extra.length === 0) return null;
  return `The open view only filters by company and person - it can't reflect ${extra.join(', ')} from this search. The full, correct list is right here.`;
};

const Mark = ({ name, logoUuid }) => (logoUuid
  ? <img className="ask-row__mark ask-row__mark--logo" src={`${LOGO_URL}/${logoUuid}`} alt="" />
  : <span className="ask-row__mark">{initials(name)}</span>);

const PersonRow = ({ person, onOpen }) => (
  <button type="button" className="ask-row" onClick={() => onOpen(person)}>
    <Mark name={person.name} />
    <span className="ask-row__main">
      <span className="ask-row__name">{person.name}</span>
      <span className="ask-row__detail">{[person.title, person.company.name].filter(Boolean).join(' · ')}</span>
    </span>
    {person.location && <span className="ask-row__place">{person.location}</span>}
  </button>
);

const CompanyRow = ({ company, onOpen }) => {
  const tier = company.tier && `Tier ${TIER_LABELS[company.tier] || company.tier}`;
  const people = `${company.people} ${company.people === 1 ? 'person' : 'people'}`;
  return (
    <button type="button" className="ask-row" onClick={() => onOpen(company)}>
      <Mark name={company.name} logoUuid={company.logo_uuid} />
      <span className="ask-row__main">
        <span className="ask-row__name">{company.name}</span>
        <span className="ask-row__detail">{[tier, company.sector, people].filter(Boolean).join(' · ')}</span>
      </span>
      {company.location && <span className="ask-row__place">{company.location}</span>}
    </button>
  );
};

// People in this search with no location on file, who the agents can go and look up
const FindLocations = ({ answer }) => {
  const [state, setState] = useState({ busy: false, message: null });
  const start = async () => {
    setState({ busy: true, message: null });
    try {
      const run = await enrichmentApi.startRun('contact', { filters: answer.filters });
      setState({ busy: false, message: `The agents are looking for ${run.total.toLocaleString()} of them. Ask again later.` });
    } catch (err) {
      setState({ busy: false, message: err.message });
    }
  };
  if (state.message) return <p className="ask-answer__note"><Icon name="search" /> {state.message}</p>;
  return (
    <button type="button" className="ask-more" disabled={state.busy} onClick={start}>
      <Icon name={state.busy ? 'loader' : 'search'} spin={state.busy} />
      Send agents to find their locations
    </button>
  );
};

const Answer = ({ turn, onRemoveFilter, onMore, onOpenPerson, onOpenCompany }) => {
  const { answer } = turn;
  return (
    <div className="ask-answer">
      {answer.understood !== false && <p className="ask-answer__headline">{headline(answer)}</p>}
      {answer.chips.length > 0 && (
        <ul className="ask-chips" aria-label="Filters">
          {answer.chips.map(chip => {
            const dropped = answer.dropped.includes(chip.field);
            return (
              <li key={chip.field} className={`ask-chip${dropped ? ' ask-chip--dropped' : ''}`}>
                <span className="ask-chip__label">{chip.label}</span> {chip.value}
                <button
                  type="button"
                  className="ask-chip__remove"
                  aria-label={`Remove ${chip.label}`}
                  onClick={() => onRemoveFilter(turn, chip.field)}
                >
                  <Icon name="x" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {answer.notes.map(note => (
        <p key={note} className="ask-answer__note"><Icon name="alert-triangle" /> {note}</p>
      ))}
      {answer.kind === 'people' && answer.unknown_locations > 0 && <FindLocations answer={answer} />}
      {answer.results.length > 0 && (
        <ul className="ask-results">
          {answer.results.map(row => (
            <li key={row.id}>
              {answer.kind === 'companies'
                ? <CompanyRow company={row} onOpen={onOpenCompany} />
                : <PersonRow person={row} onOpen={onOpenPerson} />}
            </li>
          ))}
        </ul>
      )}
      {answer.results.length < answer.total && (
        <button type="button" className="ask-more" disabled={turn.loadingMore} onClick={() => onMore(turn)}>
          {turn.loadingMore ? <Icon name="loader" spin /> : null}
          Show more ({(answer.total - answer.results.length).toLocaleString()} left)
        </button>
      )}
    </div>
  );
};

/** A box for searching in plain words. Follow-up questions build on the filters of the last answer. */
const AskSinatra = ({ theme, onOpenPerson, onOpenCompany, onApplyFilters }) => {
  // Deliberately the opposite of the page's theme, so it reads as a landmark wherever you look
  const inverseTheme = theme === 'dark' ? 'light' : 'dark';
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [turns, setTurns] = useState([]);
  const [openError, setOpenError] = useState(null);
  const inputRef = useRef(null);
  const logRef = useRef(null);
  const nextTurn = useRef(1);

  const busy = turns.some(turn => turn.loading);

  const updateTurn = useCallback((id, changes) => {
    setTurns(current => current.map(turn => (turn.id === id ? { ...turn, ...changes } : turn)));
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [turns.length, open]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === '/' && !isTyping(document.activeElement) && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const send = async (text) => {
    const question = text.trim();
    if (!question || busy) return;
    const inUse = [...turns].reverse().find(turn => turn.answer?.understood)?.answer.filters;
    const id = nextTurn.current++;
    setTurns(current => [...current, { id, question, loading: true }]);
    setDraft('');
    setOpen(true);
    setOpenError(null);
    try {
      const answer = await askApi.ask(question, inUse);
      if (answer.understood !== false) onApplyFilters?.(answer.filters);
      const note = unmappedFilterNote(answer.filters);
      updateTurn(id, { loading: false, answer: note ? { ...answer, notes: [...answer.notes, note] } : answer });
    } catch (err) {
      updateTurn(id, { loading: false, error: err.message });
    }
  };

  const removeFilter = async (turn, field) => {
    const { [field]: removed, ...filters } = turn.answer.filters;
    try {
      const result = await askApi.results(filters);
      onApplyFilters?.(filters);
      const note = unmappedFilterNote(filters);
      updateTurn(turn.id, { answer: { ...turn.answer, ...result, notes: note ? [...result.notes, note] : result.notes } });
    } catch (err) {
      updateTurn(turn.id, { error: err.message });
    }
  };

  const showMore = async (turn) => {
    const { answer } = turn;
    updateTurn(turn.id, { loadingMore: true });
    try {
      const next = await askApi.results(answer.filters, answer.page + 1);
      updateTurn(turn.id, {
        loadingMore: false,
        answer: { ...answer, page: next.page, results: [...answer.results, ...next.results] }
      });
    } catch (err) {
      updateTurn(turn.id, { loadingMore: false, error: err.message });
    }
  };

  const openPerson = async (person) => {
    setOpenError(null);
    try {
      await onOpenPerson(person.id);
    } catch (err) {
      setOpenError(err.message);
    }
  };

  const reset = () => {
    setTurns([]);
    setOpenError(null);
    onApplyFilters?.(null);
    inputRef.current?.focus();
  };

  return (
    <section
      className={`ask${open ? ' ask--open' : ''}`}
      data-theme={inverseTheme}
      aria-label="Ask Sinatra"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      {open && (
        <div className="ask-panel">
          <header className="ask-panel__header">
            <h2>Ask Sinatra</h2>
            {turns.length > 0 && (
              <button type="button" className="ask-panel__action" onClick={reset}>New search</button>
            )}
            <button type="button" className="ask-panel__close" aria-label="Close" onClick={() => setOpen(false)}>
              <Icon name="x" />
            </button>
          </header>
          <div className="ask-log" ref={logRef} role="log" aria-live="polite">
            {turns.length === 0 && (
              <div className="ask-examples">
                <p>Search people and companies in plain words.</p>
                {EXAMPLES.map(example => (
                  <button key={example} type="button" className="ask-example" onClick={() => send(example)}>
                    {example}
                  </button>
                ))}
              </div>
            )}
            {turns.map(turn => (
              <div key={turn.id} className="ask-turn">
                <p className="ask-turn__question">{turn.question}</p>
                {turn.loading && <p className="ask-answer__status"><Icon name="loader" spin /> Searching...</p>}
                {turn.error && <p className="ask-answer__error" role="alert"><Icon name="alert-triangle" /> {turn.error}</p>}
                {turn.answer && (
                  <Answer
                    turn={turn}
                    onRemoveFilter={removeFilter}
                    onMore={showMore}
                    onOpenPerson={openPerson}
                    onOpenCompany={onOpenCompany}
                  />
                )}
              </div>
            ))}
            {openError && <p className="ask-answer__error" role="alert"><Icon name="alert-triangle" /> {openError}</p>}
          </div>
        </div>
      )}
      <form
        className="ask-box"
        onSubmit={(event) => {
          event.preventDefault();
          send(draft);
        }}
      >
        <Icon name="search" />
        <input
          ref={inputRef}
          type="text"
          value={draft}
          maxLength={500}
          aria-label="Ask Sinatra"
          placeholder={turns.length ? 'Ask a follow-up, or something new' : 'Ask Sinatra: CxOs at Inference.net'}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => setOpen(true)}
        />
        <button type="submit" className="ask-box__send" aria-label="Ask" disabled={busy || !draft.trim()}>
          <Icon name={busy ? 'loader' : 'arrow-up'} spin={busy} />
        </button>
      </form>
    </section>
  );
};

export default AskSinatra;
